import React, { useState, useEffect } from 'react';
import {
  ClipboardList,
  Loader,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  Clock,
  UserPlus
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { CountingListOverviewItem } from '../../../../types/countingListCreation';
import { StatusBadge } from '../../../common/StatusBadge';

interface CountingListSelectorProps {
  onSelectList: (list: CountingListOverviewItem) => void;
}

const isUnassigned = (list: CountingListOverviewItem): boolean =>
  !list.operatorID || !list.operatorID.trim();

export const CountingListSelector: React.FC<CountingListSelectorProps> = ({ onSelectList }) => {
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const { t } = useTranslation();
  const [lists, setLists] = useState<CountingListOverviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimingBatchId, setClaimingBatchId] = useState<string | null>(null);
  const [claimMessage, setClaimMessage] = useState<string | null>(null);

  const loadLists = async () => {
    if (!user?.operatorId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await countingListCreationService.getCountingListOverview(
        currentWarehouse || undefined,
        user.operatorId
      );
      // Only show active lists (not completed or exported): status < 50
      setLists(result.filter(l => l.orderStatus < 50));
    } catch (err) {
      console.error('Error loading counting lists:', err);
      setError(t('warehouse.modules.inventory.countList.loadListsFailed', {}, 'Kunne ikke laste tellelister'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, [user?.operatorId, currentWarehouse]);

  const handleSelect = async (list: CountingListOverviewItem) => {
    if (!isUnassigned(list)) {
      onSelectList(list);
      return;
    }

    // Ledig liste — ta den atomisk før tellingen starter
    if (claimingBatchId) return;
    setClaimingBatchId(list.batchID);
    setClaimMessage(null);
    try {
      const result = await countingListCreationService.claimCountingList(list.batchID);
      if (result.success) {
        onSelectList({
          ...list,
          operatorID: user?.operatorId,
          operatorName: user?.operatorName || user?.operatorId,
          orderStatus: 10
        });
      } else {
        setClaimMessage(t(
          'warehouse.modules.inventory.countList.claimTakenBy',
          { '0': result.claimedBy || '?' },
          `Listen ble nettopp tatt av ${result.claimedBy || '?'}`
        ));
        await loadLists();
      }
    } catch (err) {
      console.error('Error claiming counting list:', err);
      setClaimMessage(t('warehouse.modules.inventory.countList.claimFailed', {}, 'Kunne ikke ta tellelisten — prøv igjen'));
      await loadLists();
    } finally {
      setClaimingBatchId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="animate-spin h-8 w-8 text-blue-500 mb-2" />
        <span className="text-sm text-gray-500">{t('warehouse.modules.inventory.countList.loadingLists', {}, 'Laster tellelister...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={loadLists}
          className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>{t('common.buttons.tryAgain', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  const myLists = lists.filter(l => !isUnassigned(l));
  const availableLists = lists.filter(isUnassigned);

  if (lists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <ClipboardList className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 mb-1">{t('warehouse.modules.inventory.countList.noListsAvailable', {}, 'Ingen tellelister tildelt deg eller ledige')}</p>
        <p className="text-xs text-gray-400">{t('warehouse.modules.inventory.countList.contactManagerForList', {}, 'Kontakt lagersjef for å få tildelt en telleliste')}</p>
        <button
          onClick={loadLists}
          className="mt-4 flex items-center space-x-1 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>{t('common.buttons.refresh', {}, 'Oppdater')}</span>
        </button>
      </div>
    );
  }

  const renderListCard = (list: CountingListOverviewItem, available: boolean) => {
    const isClaiming = claimingBatchId === list.batchID;
    return (
      <button
        key={list.batchID}
        onClick={() => handleSelect(list)}
        disabled={isClaiming}
        className={`w-full text-left bg-white border rounded-lg p-3 hover:shadow-sm transition-all group ${
          available ? 'border-green-200 hover:border-green-400' : 'hover:border-blue-300'
        } ${isClaiming ? 'opacity-70' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-medium text-sm text-gray-900 truncate">
                {list.description1 || list.batchID}
              </span>
              {available ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <UserPlus className="h-3 w-3" />
                  {t('warehouse.modules.inventory.countList.availableBadge', {}, 'Ledig')}
                </span>
              ) : (
                <StatusBadge value={list.orderStatus} statusType={1} />
              )}
            </div>
            <div className="flex items-center space-x-3 text-xs text-gray-500">
              <span className="font-mono">{list.batchID}</span>
              {list.warehouse && <span>{t('warehouse.modules.inventory.countList.warehouseLabel', { '0': list.warehouse }, `Lager: ${list.warehouse}`)}</span>}
            </div>
            {/* Progress bar */}
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span className="flex items-center space-x-1">
                  {list.completedLines === list.totalLines && list.totalLines > 0 ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <Clock className="h-3 w-3" />
                  )}
                  <span>{t('warehouse.modules.inventory.countList.linesCounted', { '0': list.completedLines, '1': list.totalLines }, `${list.completedLines} / ${list.totalLines} linjer telt`)}</span>
                </span>
                <span>{list.progressPercent}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    list.progressPercent === 100 ? 'bg-green-500' :
                    list.progressPercent > 0 ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                  style={{ width: `${Math.max(list.progressPercent, 0)}%` }}
                />
              </div>
            </div>
          </div>
          {isClaiming ? (
            <Loader className="h-5 w-5 text-blue-500 animate-spin ml-2 flex-shrink-0" />
          ) : (
            <ChevronRight className={`h-5 w-5 text-gray-400 ml-2 flex-shrink-0 ${
              available ? 'group-hover:text-green-500' : 'group-hover:text-blue-500'
            }`} />
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-gray-700">
          {t('warehouse.modules.inventory.countList.myLists', { '0': myLists.length }, `Mine tellelister (${myLists.length})`)}
        </h3>
        <button
          onClick={loadLists}
          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
          title={t('common.buttons.refresh', {}, 'Oppdater')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {claimMessage && (
        <div className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm bg-orange-50 text-orange-800 border border-orange-200">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{claimMessage}</span>
        </div>
      )}

      {myLists.length === 0 && (
        <p className="text-xs text-gray-400 px-1">
          {t('warehouse.modules.inventory.countList.noListsAssigned', {}, 'Ingen tellelister tildelt deg')}
        </p>
      )}
      {myLists.map((list) => renderListCard(list, false))}

      {availableLists.length > 0 && (
        <>
          <div className="flex items-center justify-between pt-2">
            <h3 className="text-sm font-medium text-gray-700">
              {t('warehouse.modules.inventory.countList.availableLists', { '0': availableLists.length }, `Ledige tellelister (${availableLists.length})`)}
            </h3>
          </div>
          <p className="text-xs text-gray-500 px-1 -mt-2">
            {t('warehouse.modules.inventory.countList.availableListsHint', {}, 'Trykk på en ledig liste for å ta den og starte tellingen')}
          </p>
          {availableLists.map((list) => renderListCard(list, true))}
        </>
      )}
    </div>
  );
};
